import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const CustomReportBuilder = ({ className = "" }) => {
  const [selectedTemplate, setSelectedTemplate] = useState('comprehensive');
  const [selectedCharts, setSelectedCharts] = useState(['metrics', 'trends']);
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [isBuilding, setIsBuilding] = useState(false);

  const templateOptions = [
    { value: 'comprehensive', label: 'Comprehensive Report' },
    { value: 'executive', label: 'Executive Summary' },
    { value: 'property-focus', label: 'Property-Focused' },
    { value: 'financial', label: 'Financial Analysis' },
    { value: 'custom', label: 'Custom Template' }
  ];

  const formatOptions = [
    { value: 'pdf', label: 'PDF Document' },
    { value: 'excel', label: 'Excel Workbook' },
    { value: 'powerpoint', label: 'PowerPoint Presentation' },
    { value: 'html', label: 'Web Report' }
  ];

  const chartOptions = [
    { id: 'metrics', label: 'Key Performance Metrics', description: 'Overview cards with main KPIs' },
    { id: 'trends', label: 'Survey Volume Trends', description: 'Line chart showing survey activity over time' },
    { id: 'distribution', label: 'Property Type Distribution', description: 'Pie chart of property types surveyed' },
    { id: 'geographic', label: 'Geographic Heat Map', description: 'Regional distribution of surveys' },
    { id: 'accuracy', label: 'Valuation Accuracy', description: 'Surveyor performance metrics' },
    { id: 'revenue', label: 'Revenue Analysis', description: 'Financial performance charts' }
  ];

  const handleChartToggle = (chartId) => {
    setSelectedCharts(prev => 
      prev?.includes(chartId) 
        ? prev?.filter(id => id !== chartId)
        : [...prev, chartId]
    );
  };

  const handleBuildReport = async () => {
    setIsBuilding(true);
    
    // Simulate report building
    setTimeout(() => {
      setIsBuilding(false);
      // Custom report built with template, charts, and format
    }, 3000);
  };

  const handleSaveTemplate = () => {
    // Template saved with template, charts, and format
  };

  return (
    <div className={`bg-card rounded-lg border border-border p-6 ${className}`}>
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="Layout" size={20} color="var(--color-primary)" />
        <h3 className="text-lg font-semibold text-foreground">Custom Report Builder</h3>
      </div>
      <div className="space-y-6">
        {/* Template Selection */}
        <div>
          <Select
            label="Report Template"
            description="Choose a base template for your report"
            options={templateOptions}
            value={selectedTemplate}
            onChange={setSelectedTemplate}
          />
        </div>

        {/* Chart Selection */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Include Charts & Visualizations</h4>
          <div className="space-y-3">
            {chartOptions?.map((chart) => (
              <div key={chart?.id} className="flex items-start space-x-3 p-3 bg-muted rounded-md">
                <Checkbox
                  checked={selectedCharts?.includes(chart?.id)}
                  onChange={() => handleChartToggle(chart?.id)}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{chart?.label}</p>
                  <p className="text-xs text-muted-foreground">{chart?.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Format Selection */}
        <div>
          <Select
            label="Export Format"
            description="Choose the output format for your report"
            options={formatOptions}
            value={selectedFormat}
            onChange={setSelectedFormat}
          />
        </div>

        {/* Preview Section */}
        <div className="p-4 bg-muted rounded-lg">
          <div className="flex items-center space-x-2 mb-3">
            <Icon name="Eye" size={16} color="var(--color-muted-foreground)" />
            <h5 className="text-sm font-medium text-foreground">Report Preview</h5>
          </div>
          <div className="text-xs text-muted-foreground space-y-1">
            <p>• Template: {templateOptions?.find(t => t?.value === selectedTemplate)?.label}</p>
            <p>• Charts: {selectedCharts?.length} visualization{selectedCharts?.length !== 1 ? 's' : ''} selected</p>
            <p>• Format: {formatOptions?.find(f => f?.value === selectedFormat)?.label}</p>
            <p>• Estimated size: ~2.4 MB</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="default"
            onClick={handleBuildReport}
            loading={isBuilding}
            iconName="Download"
            iconPosition="left"
            className="flex-1"
          >
            {isBuilding ? 'Building Report...' : 'Build & Download'}
          </Button>
          
          <Button
            variant="outline"
            onClick={handleSaveTemplate}
            iconName="Save"
            iconPosition="left"
          >
            Save Template
          </Button>
          
          <Button
            variant="ghost"
            onClick={() => {
              // Preview functionality
              {/* Preview report action */}
            }}
            iconName="Eye"
            iconPosition="left"
          >
            Preview
          </Button>
        </div>

        {/* Quick Templates */}
        <div className="pt-4 border-t border-border">
          <h5 className="text-sm font-medium text-foreground mb-3">Quick Templates</h5>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedTemplate('executive');
                setSelectedCharts(['metrics', 'trends']);
                setSelectedFormat('pdf');
              }}
              className="justify-start text-xs"
            >
              Executive Dashboard
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedTemplate('comprehensive');
                setSelectedCharts(['metrics', 'trends', 'distribution', 'geographic']);
                setSelectedFormat('pdf');
              }}
              className="justify-start text-xs"
            >
              Full Analytics Report
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedTemplate('financial');
                setSelectedCharts(['metrics', 'revenue']);
                setSelectedFormat('excel');
              }}
              className="justify-start text-xs"
            >
              Financial Summary
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedTemplate('property-focus');
                setSelectedCharts(['distribution', 'geographic']);
                setSelectedFormat('powerpoint');
              }}
              className="justify-start text-xs"
            >
              Property Analysis
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomReportBuilder;